import {
  allQuestions,
  findQuestion,
  screens,
  type ClientSessionResponse,
  type EngineState,
  type Question,
  type Questionnaire,
  type Screen,
} from '@aera/schema';

/**
 * The client flow as a list of steps, recomputed from the answers on every change (C2 appears once C1
 * has a pick, G2 disappears for frontline staff). Nothing here names a question id: the open-floor
 * question comes from the questionnaire's engine config, contact and consent screens from their types,
 * interstitials from `module.interstitial`, so the overseas track runs through the same code.
 */
export type Step =
  | { id: 'welcome'; kind: 'welcome' }
  | { id: 'consent:processing'; kind: 'consent'; consent: 'processing' }
  | { id: 'consent:crossBorder'; kind: 'consent'; consent: 'crossBorder' }
  | { id: 'declined'; kind: 'declined' }
  | { id: string; kind: 'screen'; screen: Screen; index: number; total: number }
  | { id: string; kind: 'interstitial'; module: string; text: { zh: string; en: string } }
  | { id: 'followups'; kind: 'followups' }
  | { id: 'memberCheck'; kind: 'memberCheck' }
  | { id: 'generating'; kind: 'generating' }
  | { id: 'brief'; kind: 'brief' };

export type StepKind = Step['kind'];

export const screenStepId = (screen: Screen) => `q:${screen.key}`;

/**
 * The question whose submission calls /complete: the engine config's, else (a version stored before the
 * engine config existed) the last free-text question.
 */
export function openFloorQuestionId(questionnaire: Questionnaire): string | null {
  const configured = (questionnaire.engine as Questionnaire['engine'] | undefined)?.openFloorQuestion;
  if (configured) return configured;
  const texts = allQuestions(questionnaire).filter((q) => q.type === 'text');
  return texts[texts.length - 1]?.id ?? null;
}

export const isContactType = (q: Pick<Question, 'type'>) => q.type === 'contact' || q.type === 'checkbox';

/**
 * The question screens, then the follow-ups and the member check, then the contact screens (I4, I5):
 * agreeing to be contacted is the last thing the client is asked. Screen `index` follows that order, so
 * the progress bar never moves back.
 */
export function buildSteps(state: EngineState, opts: { memberCheck: boolean }): Step[] {
  const steps: Step[] = [
    { id: 'welcome', kind: 'welcome' },
    { id: 'consent:processing', kind: 'consent', consent: 'processing' },
    { id: 'consent:crossBorder', kind: 'consent', consent: 'crossBorder' },
  ];
  const all = screens(state);
  const contactOnly = (screen: Screen) =>
    screen.questionIds.every((id) => {
      const q = findQuestion(state.questionnaire, id);
      return !!q && isContactType(q);
    });
  const questions = all.filter((s) => !contactOnly(s));
  const contact = all.filter(contactOnly);
  const total = all.length;
  const modules = new Map(state.questionnaire.modules.map((m) => [m.id, m]));
  questions.forEach((screen, index) => {
    steps.push({ id: screenStepId(screen), kind: 'screen', screen, index, total });
    const next = questions[index + 1];
    const mod = modules.get(screen.module as Questionnaire['modules'][number]['id']);
    if (mod?.interstitial && (!next || next.module !== screen.module)) {
      steps.push({ id: `int:${mod.id}`, kind: 'interstitial', module: mod.id, text: mod.interstitial });
    }
  });
  steps.push({ id: 'followups', kind: 'followups' });
  if (opts.memberCheck) steps.push({ id: 'memberCheck', kind: 'memberCheck' });
  contact.forEach((screen, i) => steps.push({ id: screenStepId(screen), kind: 'screen', screen, index: questions.length + i, total }));
  steps.push({ id: 'generating', kind: 'generating' }, { id: 'brief', kind: 'brief' });
  return steps;
}

type SessionView = ClientSessionResponse['session'];

/** Consent steps already given are passed over in both directions. */
function passable(step: Step, session: SessionView): boolean {
  if (step.kind !== 'consent') return false;
  return step.consent === 'processing' ? session.consent.processing : session.consent.crossBorder;
}

export function nextStepId(steps: Step[], currentId: string, session: SessionView): string | null {
  const i = steps.findIndex((s) => s.id === currentId);
  for (let j = i + 1; j < steps.length; j++) {
    if (!passable(steps[j]!, session)) return steps[j]!.id;
  }
  return null;
}

/**
 * Back never lands on an interstitial (it auto-advances) or on the generating screen, nor on a step in
 * `skip` (the follow-ups when there are none to ask: that screen would move straight on again).
 */
export function prevStepId(steps: Step[], currentId: string, session: SessionView, skip: ReadonlySet<string> = new Set()): string | null {
  const i = steps.findIndex((s) => s.id === currentId);
  for (let j = i - 1; j >= 0; j--) {
    const s = steps[j]!;
    if (s.kind === 'interstitial' || passable(s, session) || skip.has(s.id)) continue;
    return s.id;
  }
  return null;
}

export function stepForQuestion(steps: Step[], questionId: string | null): Step | undefined {
  if (!questionId) return undefined;
  return steps.find((s) => s.kind === 'screen' && s.screen.questionIds.includes(questionId));
}

/**
 * Saved as currentQuestionId once the respondent is past the questions, so a reload lands back on the
 * follow-ups or the member check rather than on the open floor (I3). The '@' keeps them apart
 * from question ids; the API takes up to 10 characters.
 */
export const RESUME_MARK = { followups: '@followups', memberCheck: '@check' } as const;

/** Where a returning respondent lands: the saved screen, or the step their session status implies. */
export function resumeStepId(steps: Step[], session: SessionView, questionnaire: Questionnaire): string {
  if (session.status === 'anonymised') return 'welcome';
  if (session.consent.declined && !session.consent.crossBorder) return 'declined';
  if (!session.consent.processing || !session.consent.crossBorder || session.status === 'invited') return 'welcome';
  if (session.status === 'complete') return session.briefToken ? 'brief' : 'generating';
  if (['generating', 'awaiting_review', 'failed'].includes(session.status)) return 'generating';
  const mark = session.currentQuestionId;
  // The member check needs /complete to have gone through; before that the follow-up step retries it.
  if (mark === RESUME_MARK.memberCheck && session.status === 'followups') return 'memberCheck';
  if (mark === RESUME_MARK.followups || mark === RESUME_MARK.memberCheck) return 'followups';
  const saved = stepForQuestion(steps, session.currentQuestionId);
  if (session.status === 'followups') {
    // After the open floor come the follow-ups, the member check and the contact screens: a saved contact
    // screen is where they were; anything earlier means they finished the questions. A session saved on a
    // contact screen under the old order (contact before the follow-ups; in flight at the deploy) still has
    // follow-ups to answer and no member-check decision: it goes to the follow-ups first.
    const openFloor = stepForQuestion(steps, openFloorQuestionId(questionnaire));
    const savedIndex = saved ? steps.indexOf(saved) : -1;
    const oldOrder = session.followups.some((f) => !f.answer) && !(session.memberCheck ?? []).some((l) => l.confirmed !== null);
    if (saved && openFloor && savedIndex > steps.indexOf(openFloor) && !oldOrder) return saved.id;
    return 'followups';
  }
  if (saved) return saved.id;
  return steps.find((s) => s.kind === 'screen')?.id ?? 'followups';
}

/** Fast through the quick chip screens, slower later: the progress-indicator pattern that reduced drop-off. */
export const displayedProgress = (p: number) => 1 - (1 - Math.max(0, Math.min(1, p))) ** 2;
